import { create } from 'zustand';
import { WsEvent } from '@voice-assistant/shared';

// ════════════════════════════════════════════════════════════════════════════
// Wave / plans FSM store — platform-isolation #1657 Phase 2b, Slice 2b-waves.
//
// Drains the LAST two `useSocket.ts` state-transition CustomEvent dispatches
// (`va:wave-event`, `va:plans-changed`) off the global event bus (R-A2). Before
// this slice, useSocket re-dispatched 6 wave WsEvents as `va:wave-event` and 8
// plan-pipeline WsEvents as `va:plans-changed`; PlansTab + SessionListView each
// `window.addEventListener`-ed them and synthesised LOCAL component state
// (`activeWave`, `awaitingDeploy`). This store TAKES that live-orchestration
// state so the components become pure renderers of store state — the workspace
// Frontend FSM pattern (server-config/docs/frontend-fsm-pattern.md):
//   1. Explicit FSM        — the `phase` union + the exported TRANSITIONS table.
//   2. Single store        — wave lifecycle (PlansTab) + deploy-gate flag
//                            (SessionListView) + plans-changed revision live in
//                            ONE store; each consumer reads its own slice via a
//                            fine-grained selector (so a wave event that doesn't
//                            touch a consumer's slice never re-renders it).
//   3. Server-authoritative — reconcileActiveWave() is the truth signal from the
//                            REST sensors (GET /plans/wave/active on mount, the
//                            POST /plans/wave/start response). It CLEARS on an
//                            empty response — never synthesises a lingering wave.
//   4. Named actions        — one named action per intent (onWaveEvent /
//                            onPlansChanged / reconcileActiveWave / closeWave),
//                            called DIRECTLY from useSocket (the Phase-2a
//                            docTtsStore pattern), never via dispatchEvent.
//   5. Setpoint evals       — setpoint-evals/wave-store/ drives THIS store
//                            (simulator + SE-WS-* scenarios).
//
// NOT origin-keyed: at most one wave executes per user (the backend enforces 409
// on a second), so the store is a singleton, not a Map<sessionId>.
//
// NOT persisted: the wave is server-authoritative. PlansTab re-fetches the active
// wave on every mount (reconcileActiveWave), and `awaitingDeploy` / `plansRevision`
// are transient. Persisting a live phase would risk showing a stale "executing"
// panel after a reload with no live confirmation — exactly what pattern rule 3
// forbids. (devPreviewStore persists metadata only + demotes phase on rehydrate;
// here there is no metadata worth persisting, so we skip persist entirely.)
// ════════════════════════════════════════════════════════════════════════════

// ─── State types ───────────────────────────────────────────────────────────────

/**
 * FSM phase for the (single) active wave execution.
 *
 *   idle       no wave is executing (never started, or the last one was closed /
 *              reconciled away). PlansTab shows no WaveProgressPanel.
 *   executing  a wave is live (wave_started / wave_advanced, or a server reconcile
 *              that returned a non-terminal wave).
 *   completed  the wave finished (wave_complete with a wave in flight).
 *   failed     the wave failed (wave_failed).
 *
 * Full transition table is exported as `TRANSITIONS` below (R-A1). The reducer is
 * deliberately PERMISSIVE (never a gatekeeper): the wire can deliver wave_started
 * after a previous wave completed (completed → executing for the new wave), or
 * wave_advanced with no prior wave after a reconnect (idle → executing) — exactly
 * as the former imperative `prev ? {...prev,...data} : data` handler allowed. The
 * table documents the canonical legs; it does not constrain the `from` state.
 */
export type WavePhase = 'idle' | 'executing' | 'completed' | 'failed';

/**
 * The wave-execution snapshot as PlansTab passes it to `WaveProgressPanel` (whose
 * prop is its local, non-exported `WaveExecution` interface). Deliberately `any`,
 * for two reasons the advisor flagged: (1) the wire delivers a FULL object on
 * wave_started / the REST sensors but a PARTIAL patch on wave_advanced (merged onto
 * the live wave), so a stricter type would force casts that paper over partial
 * payloads; (2) the former PlansTab state was exactly `useState<any | null>`, so
 * `any` keeps this conversion byte-faithful and the `WaveProgressPanel` prop
 * assignment cast-free. Importing the panel's `WaveExecution` here would be a
 * backwards `state/ → components/` dependency (the shared layer must not depend on
 * the view layer), so we don't.
 */
export type WaveSnapshot = any;

export interface WaveStore {
  // ── Wave FSM (formerly PlansTab `activeWave` local state) ──────────────────
  phase: WavePhase;
  /** The live wave-execution object, or null when no wave is active. */
  activeWave: WaveSnapshot | null;

  // ── Deploy-gate flag (formerly SessionListView `awaitingDeploy` local state) ─
  /** True between PHASE_DEPLOY_REQUIRED and PHASE_DEPLOY_DETECTED/complete/failed.
   *  Drives the `🚀` marker on the Agent tab button. */
  awaitingDeploy: boolean;

  // ── Plans-changed signal (formerly the `va:plans-changed` CustomEvent) ──────
  /** Monotonic counter bumped on every plan-pipeline WsEvent. PlansTab subscribes
   *  and re-fetches (plans + queue stats) when it changes — replaces the old
   *  `va:plans-changed` listener. NOT part of the phase FSM. */
  plansRevision: number;

  // ── Named actions (R-A2 — called directly from useSocket, never dispatched) ─
  /** Wave-lifecycle + deploy-gate transition. `detail` mirrors the former
   *  `va:wave-event` CustomEvent payload (`{ event: WsEvent, data }`). */
  onWaveEvent: (detail: { event: string; data: WaveSnapshot }) => void;
  /** Plan-pipeline change signal — bumps `plansRevision`. `detail` is ignored
   *  (the old PlansTab handler ignored it too; it only re-fetched). */
  onPlansChanged: (detail?: unknown) => void;
  /** Server-authoritative reconcile (GET /plans/wave/active on mount, POST
   *  /plans/wave/start response). A wave WITH an id sets it active; null / no-id
   *  CLEARS — catching a completion that landed while PlansTab was unmounted
   *  (the local-`useState` re-init that used to do this is gone). */
  reconcileActiveWave: (wave: WaveSnapshot | null) => void;
  /** User closed the WaveProgressPanel → clear the active wave (panel hides). */
  closeWave: () => void;
}

// ─── Transition table (R-A1) ────────────────────────────────────────────────────
//
// `setpoint-evals/wave-store/SE-WS-02-fsm-coverage` validates that every distinct
// `trigger` here is covered by a simulator scenario's `// SE-COVERS:` marker —
// adding a transition row MUST add a covering scenario. Triggers are the named
// outcomes of the store's actions (the wave-event kind, or a reconcile outcome).
// `phase_deploy_required` / `phase_deploy_detected` and `plansRevision` are
// ORTHOGONAL to the phase FSM (they don't move `phase`), so they are NOT rows
// here — their behaviour is covered by separate (uncounted) simulator scenarios.

export const TRANSITIONS: Array<{ from: WavePhase; to: WavePhase; trigger: string }> = [
  { from: 'idle', to: 'executing', trigger: 'wave_started' },
  { from: 'executing', to: 'executing', trigger: 'wave_advanced' },
  { from: 'executing', to: 'completed', trigger: 'wave_complete' },
  { from: 'executing', to: 'failed', trigger: 'wave_failed' },
  { from: 'idle', to: 'executing', trigger: 'reconcile:active' },
  { from: 'executing', to: 'idle', trigger: 'reconcile:idle' },
  { from: 'completed', to: 'idle', trigger: 'closeWave' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────────

/** Derive the FSM phase from a server-authoritative wave snapshot's own status. */
function phaseFromWave(wave: WaveSnapshot): WavePhase {
  if (wave.status === 'completed') return 'completed';
  if (wave.status === 'failed') return 'failed';
  return 'executing';
}

// ─── Store ─────────────────────────────────────────────────────────────────────

export const useWaveStore = create<WaveStore>()((set) => ({
  phase: 'idle',
  activeWave: null,
  awaitingDeploy: false,
  plansRevision: 0,

  onWaveEvent: ({ event, data }) =>
    set((s) => {
      switch (event) {
        // wave_started: merge onto the live wave, else adopt the payload — verbatim
        // the former `prev ? {...prev,...data} : data`.
        case WsEvent.WAVE_STARTED:
          return {
            activeWave: s.activeWave ? { ...s.activeWave, ...data } : data,
            phase: 'executing',
          };
        // wave_advanced: same merge, AND clear the deploy gate. A wave advancing to the
        // next phase implies the prior phase's deploy gate has resolved (the gate pauses
        // BEFORE advancing). This also closes the Skip-Gate hole: `skipDeployGate` on the
        // backend resolves the gate WITHOUT emitting PHASE_DEPLOY_DETECTED, so the gate
        // marker would otherwise stay stuck now that awaitingDeploy lives in the surviving
        // store instead of a per-mount `useState(false)`. (Backend follow-up logged.)
        case WsEvent.WAVE_ADVANCED:
          return {
            activeWave: s.activeWave ? { ...s.activeWave, ...data } : data,
            phase: 'executing',
            awaitingDeploy: false,
          };
        // wave_failed: same merge (PlansTab handled it in the same branch as
        // started/advanced — no forced status), AND clear the deploy gate
        // (SessionListView cleared awaitingDeploy on wave_failed).
        case WsEvent.WAVE_FAILED:
          return {
            activeWave: s.activeWave ? { ...s.activeWave, ...data } : data,
            phase: 'failed',
            awaitingDeploy: false,
          };
        // wave_complete: `prev ? {...prev, status:'completed', ...data} : null`
        // (note: `...data` AFTER status:'completed' — a status in data WINS, exactly
        // as before), AND clear the deploy gate (SessionListView).
        case WsEvent.WAVE_COMPLETE: {
          const next: WaveSnapshot | null = s.activeWave
            ? { ...s.activeWave, status: 'completed', ...data }
            : null;
          return { activeWave: next, phase: next ? 'completed' : 'idle', awaitingDeploy: false };
        }
        // deploy-gate flag only (SessionListView) — phase unchanged.
        case WsEvent.PHASE_DEPLOY_REQUIRED:
          return { awaitingDeploy: true };
        case WsEvent.PHASE_DEPLOY_DETECTED:
          return { awaitingDeploy: false };
        // Any other event riding va:wave-event was ignored by both old handlers.
        default:
          return {};
      }
    }),

  onPlansChanged: () => set((s) => ({ plansRevision: s.plansRevision + 1 })),

  reconcileActiveWave: (wave) =>
    set(() => {
      // Server says no active wave → CLEAR (advisor fix: without this the global
      // store would keep a stale completed wave after a PlansTab remount, since
      // today's clear came from the local-`useState` re-init we removed).
      if (!wave || !wave.id) {
        return { activeWave: null, phase: 'idle', awaitingDeploy: false };
      }
      // Server-authoritative: a wave PAUSED at a deploy gate carries
      // `pauseReason: 'deploy-gate:<sha>'` (wave-coordinator.service.ts). Deriving
      // awaitingDeploy here makes the gate marker survive a reload / self-heal on
      // remount — the event-only path (PHASE_DEPLOY_REQUIRED/DETECTED) can miss a
      // clear (e.g. Skip Gate) or be lost across a page reload. Mirrors the
      // WaveProgressPanel `isAwaitingDeploy` derivation.
      const awaitingDeploy =
        typeof wave.pauseReason === 'string' && wave.pauseReason.startsWith('deploy-gate:');
      return { activeWave: wave, phase: phaseFromWave(wave), awaitingDeploy };
    }),

  closeWave: () => set(() => ({ activeWave: null, phase: 'idle' })),
}));

// ─── Hook selectors — the ONLY supported way to read state in React ─────────────

/** Subscribe to the active wave (PlansTab → WaveProgressPanel `wave` prop). */
export function useActiveWave(): WaveSnapshot | null {
  return useWaveStore((s) => s.activeWave);
}

/** Subscribe to the deploy-gate flag (SessionListView Agent-tab `🚀` marker). */
export function useAwaitingDeploy(): boolean {
  return useWaveStore((s) => s.awaitingDeploy);
}

/** Subscribe to the plans-changed revision (PlansTab re-fetch trigger). */
export function usePlansRevision(): number {
  return useWaveStore((s) => s.plansRevision);
}

/** Subscribe to the wave FSM phase (available for the desktop shell, #1657). */
export function useWavePhase(): WavePhase {
  return useWaveStore((s) => s.phase);
}
